/**
 * #109 · Stock de etiquetas de XYVONA y entrega a un cliente.
 *
 * Los rollos que XYVONA le compró al proveedor y todavía no son de nadie:
 * muestras, demos, el colchón para el próximo pedido. Se generan con el mismo
 * flujo de Etiquetas que usa cualquier marca —no hay una segunda
 * implementación de la generación de códigos, y no puede haberla: es la que
 * firma con la clave HMAC.
 *
 * Entregar mueve el rollo Y sus unidades al cliente. Que eso sea posible con
 * etiquetas ya IMPRESAS depende de que el código no lleve el tenant adentro
 * (ADR-003/004). El diálogo lo dice con todas las letras porque no se puede
 * deshacer desde acá.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { useApiQuery } from '@/api/hooks';
import type { Schemas } from '@/api/types';
import { useApi } from '@/api/useApi';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { ErrorState, QueryBoundary } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { Dialog } from '../products/ui/Dialog';
import { SelectField } from '../products/ui/fields';
import { hqKeys } from './keys';

type Rollo = Schemas['StockRollOut'];
type Cliente = Schemas['TenantOut'];
type Entregado = Schemas['RollAssignedOut'];

const BACKOFFICE_SLUG = 'xyvona';

export function StockPage(): ReactNode {
  const { t, fmt } = useI18n();
  const api = useApi();
  const queryClient = useQueryClient();

  const [verTodos, setVerTodos] = useState(false);
  const [entregando, setEntregando] = useState<Rollo | null>(null);
  const [destino, setDestino] = useState('');

  const rollos = useApiQuery<Rollo[]>(
    hqKeys.stock(!verTodos),
    '/api/v1/backoffice/stock',
    { solo_libres: !verTodos },
  );
  const clientes = useApiQuery<Cliente[]>(hqKeys.clients(''), '/api/v1/backoffice/tenants');

  const entregar = useMutation<Entregado, unknown, { roll: number; tenant: number }>({
    mutationFn: ({ roll, tenant }) =>
      api.post<Entregado>(`/api/v1/backoffice/stock/${roll}/assign`, { tenant_id: tenant }),
    onSuccess: () => {
      setEntregando(null);
      setDestino('');
      void queryClient.invalidateQueries({ queryKey: hqKeys.all });
    },
  });

  // Sólo clientes activos: entregarle un rollo a una marca suspendida es
  // mandar etiquetas a quien no puede activarlas.
  const destinos = (clientes.data ?? [])
    .filter((cliente) => cliente.is_active)
    .map((cliente) => ({ value: String(cliente.tenant_id), label: cliente.legal_name }));

  return (
    <div>
      <Card>
        <CardHeader
          title={t('stock.title')}
          description={t('stock.description')}
          actions={
            <Button size="sm" variant="ghost" onClick={() => { setVerTodos((v) => !v); }}>
              {t('stock.showAll')}
            </Button>
          }
        />
        <CardBody className="p-0">
          <QueryBoundary query={rollos} empty={{ title: t('stock.empty') }}>
            {(filas) => (
              <Table caption={t('stock.title')}>
                <thead>
                  <tr>
                    <Th>{t('stock.col.roll')}</Th>
                    <Th>{t('stock.col.order')}</Th>
                    <Th>{t('stock.col.supplier')}</Th>
                    <Th className="text-right">{t('stock.col.units')}</Th>
                    <Th>{t('stock.col.range')}</Th>
                    <Th>{t('stock.col.owner')}</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {filas.map((fila) => {
                    const libre = fila.tenant_slug === BACKOFFICE_SLUG;
                    return (
                      <tr key={fila.roll_id}>
                        <Td className="font-mono text-xs">{fila.roll_code ?? fila.roll_id}</Td>
                        <Td className="text-xs">{fila.order_code ?? '—'}</Td>
                        <Td className="text-xs">{fila.supplier ?? '—'}</Td>
                        <Td className="text-right tabular-nums">
                          {fmt.number(fila.unit_count)}
                        </Td>
                        <Td className="font-mono text-xs text-ink-muted">
                          {fila.serial_from}–{fila.serial_to}
                        </Td>
                        <Td>
                          {libre ? (
                            <Badge tone="neutral">{t('hq.free')}</Badge>
                          ) : (
                            <span className="text-xs">{fila.tenant_slug}</span>
                          )}
                        </Td>
                        <Td className="text-right">
                          {libre && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                entregar.reset();
                                setDestino(destinos[0]?.value ?? '');
                                setEntregando(fila);
                              }}
                            >
                              {t('stock.assign')}
                            </Button>
                          )}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </QueryBoundary>
        </CardBody>
      </Card>

      <Dialog
        open={entregando !== null}
        onClose={() => { setEntregando(null); }}
        title={t('stock.assign.title')}
        description={entregando?.roll_code ?? undefined}
        tone="critical"
        footer={
          <>
            <Button size="sm" variant="ghost" onClick={() => { setEntregando(null); }}>
              {t('form.cancel')}
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={destino === '' || entregar.isPending}
              onClick={() => {
                if (entregando !== null && destino !== '') {
                  entregar.mutate({ roll: entregando.roll_id, tenant: Number(destino) });
                }
              }}
            >
              {t('stock.assign.submit')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-ink-muted">{t('stock.assign.warning')}</p>
          <SelectField
            label={t('stock.assign.client')}
            value={destino}
            onChange={setDestino}
            options={destinos}
          />
          {entregar.error !== null && entregar.error !== undefined && (
            <ErrorState error={entregar.error} />
          )}
        </div>
      </Dialog>
    </div>
  );
}
